import { PayloadAction } from '@reduxjs/toolkit';
import {
  AccountModel,
  AnimeModel,
  GenderFilterEnum,
  GroupModel,
  Permissions,
  PriceModel,
  UserModel,
} from '../models';
import { ReactElement } from 'react';

export enum ContentPriorityType {
  ADULT = 'adult',
  OTHER = 'other',
  NONE = 'none',
}

type DecryptKeys = { [index: number]: string };

type Card = {
  last4: string;
  brand: string;
  id: number;
};

interface ShowFeatures {
  smartFeed?: boolean;
  followingFeed?: boolean;
  im?: boolean;
  paidPost?: boolean;
  paidMessage?: boolean;
  eroticSubscription?: boolean;
  statisticsPro?: boolean;
}

export interface MainState {
  users: { [index: number]: UserModel };
  groups: { [index: number]: GroupModel };
  anime: { [index: number]: AnimeModel };
  accounts: AccountModel[];
  contentPriority: ContentPriorityType;
  genderFilter: GenderFilterEnum;
  newDialogs: number;
  hasNotify: boolean;
  isNotifyEnabled: boolean;
  playingVideo: string;
  isMainPageLoading: boolean;
  mainPagePosts: number[];
  mainPageGroups: number[];
  permissions: Permissions;
  popup: ReactElement | null;
  hasSubscription: boolean;
  hasPassword: boolean;
  hasBot: boolean;
  topUpAddress: string;
  subscriptionPrice: number;
  subscriptionRubPrice: number;
  hasPersonalSubscription: boolean;
  decryptKeyLegacy: string;
  decryptKeys: DecryptKeys;
  balance: number;
  eurBalance: number;
  placeBalance: number;
  canWithdraw: boolean;
  mVerified: boolean;
  adultPrice: PriceModel;
  otherPrice: PriceModel;
  isWatermarkDisabled: boolean;
  canUseSponsorSubscription: boolean;
  tonAddress?: string;
  card: Card | null;
  canBroadcastMessages: boolean;
  showInstallPrompt: boolean;
  eurTopUpFeeAction: number;
  feePercent: number;
  priceLimits: { [category: string]: number };
  offline: boolean;
  incognito: boolean;
  error: 'serverError' | null;
  showFeatures: ShowFeatures;
  isFraudAccepted: boolean;
  managerLink: string;
  isCreateMenuShown: boolean;
  agePopoverHidden: boolean;
  countryCode: string;
  payInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  isModer: boolean;
  isCryptoEnabled: boolean;
  isBlackFriday2025Enabled: boolean;
  shouldBindEmail: boolean;
}

export type SetUserAction = PayloadAction<Partial<UserModel>>;
export type SetCard = PayloadAction<Card | null>;
export type SetAccountsAction = PayloadAction<AccountModel[]>;
export type SetCanBroadcastMessagesAction = PayloadAction<boolean>;
export type SetUsersAction = PayloadAction<{ [index: number]: UserModel }>;
export type SetUsersArrayAction = PayloadAction<UserModel[]>;
export type SetGroupAction = PayloadAction<Partial<GroupModel>>;
export type SetPermissionsAction = PayloadAction<Permissions>;
export type SetGroupsAction = PayloadAction<{ [index: number]: GroupModel }>;
export type SetNewDialogsAction = PayloadAction<number>;
export type SetHasNotifyAction = PayloadAction<boolean>;
export type SetNotifyEnabledAction = PayloadAction<boolean>;
export type SetPlayingVideoAction = PayloadAction<string>;
export type SetMainPageDataAction = PayloadAction<{
  groups: number[];
  posts: number[];
}>;
export type SetContentPriorityAction = PayloadAction<ContentPriorityType>;
export type SetGenderFilterAction = PayloadAction<GenderFilterEnum>;
export type SetAnimesAction = PayloadAction<{ [index: number]: AnimeModel }>;
export type SetPopupAction = PayloadAction<ReactElement | null>;
export type SetHasSubscriptionAction = PayloadAction<boolean>;
export type SetHasPasswordAction = PayloadAction<boolean>;
export type SetCanWithdrawAction = PayloadAction<boolean>;
export type SetTopUpAddressAction = PayloadAction<string>;
export type SetHasPersonalSubscriptionAction = PayloadAction<boolean>;
export type SetSubscriptionPriceAction = PayloadAction<{
  ton: number;
  rubles: number;
}>;
export type SetDecryptKeyActionLegacy = PayloadAction<string>;
export type SetDecryptKeyAction = PayloadAction<{ id: number; decryptKey: string }>;
export type DeleteDecryptKeyAction = PayloadAction<number>;
export type SetDecryptKeysAction = PayloadAction<DecryptKeys>;
export type SetBalanceAction = PayloadAction<number>;
export type SetEurBalanceAction = PayloadAction<number>;
export type SetMVerifiedAction = PayloadAction<boolean>;
export type SetHasBotAction = PayloadAction<boolean>;
export type SetSubscriptionsPricesAction = PayloadAction<{
  adult: PriceModel;
  other: PriceModel;
}>;
export type SetIsWatermarkDisabledAction = PayloadAction<boolean>;
export type SetCanUseSponsorSubscriptionAction = PayloadAction<boolean>;
export type SetTonAddressAction = PayloadAction<string>;
export type SetShowInstallPromptAction = PayloadAction<boolean>;
export type SetEurTopUpFeeAction = PayloadAction<number>;
export type SetFeePercentAction = PayloadAction<number>;
export type SetPriceLimitsAction = PayloadAction<{ [category: string]: number }>;
export type SetOfflineAction = PayloadAction<boolean>;
export type SetIncognitoEnabledAction = PayloadAction<boolean>;
export type EditIncognitoEnabledAction = PayloadAction<boolean>;
export type SetErrorAction = PayloadAction<MainState['error']>;
export type SetShowFeaturesAction = PayloadAction<ShowFeatures>;
export type SetFeatureAsShownAction = PayloadAction<keyof ShowFeatures>;
export type SetFraudAcceptedAction = PayloadAction<boolean>;
export type SetManagerLinkAction = PayloadAction<string>;
export type SetCreateMenuShownAction = PayloadAction<boolean>;
